import { useRef, useState } from "react"

import { Flex, Space, Typography } from "antd"
import cls from "classname"
import { QRCodeSVG } from "qrcode.react"

import ButtonComponent from "../../Button"
import InputComponent from "../../Input"
import TwoFactorSecretModal from "../TwoFactorSecretModal/TwoFactorSecretModal"

import "./styles.less"

const { Text } = Typography

const ScanCodeBlock = ({
  t,
  qrCodeSrc,
  otpauthUrl,
  setupKey,
  onSuccess,
  onSubmit,
  buttonText = "Save",
  otpError = "",
  isSubmitting = false,
  isQrCentered = false,
  isSaveButtonBlock = false,
  hasSaveButtonTopMargin = false,
  showTrustedDevice = true
}) => {
  const [secretModalOpen, setSecretModalOpen] = useState(false)
  const [authCodeDigits, setAuthCodeDigits] = useState(Array(6).fill(""))
  const [rememberDevice, setRememberDevice] = useState(true)
  const inputRefs = useRef([])

  const codeValue = authCodeDigits.join("")

  const handleAuthCodeChange = (index, event) => {
    const digit = event.target.value.replace(/\D/g, "").slice(-1)
    const nextDigits = [...authCodeDigits]

    nextDigits[index] = digit
    setAuthCodeDigits(nextDigits)

    if (digit && index < 5) {
      inputRefs.current[index + 1]?.focus?.()
    }
  }

  const handleAuthCodeKeyDown = (index, event) => {
    if (event.key !== "Backspace") return

    if (authCodeDigits[index]) {
      event.preventDefault()

      const nextDigits = [...authCodeDigits]
      nextDigits[index] = ""
      setAuthCodeDigits(nextDigits)

      if (index > 0) {
        inputRefs.current[index - 1]?.focus?.()
      }

      return
    }

    if (index > 0) {
      inputRefs.current[index - 1]?.focus?.()
    }
  }

  const handleClick = () => {
    if (onSubmit) {
      onSubmit({
        otp: codeValue,
        rememberDevice
      })
      return
    }

    if (onSuccess) {
      onSuccess()
    }
  }

  return (
    <>
      <Space size={16} direction="vertical" className="security-2fa-block">
        <Space size={4} direction="vertical" className="security-2fa-block__header">
          <Text strong>{t("Scan the QR code")}</Text>
          <Text type="secondary">
            {t("Use an authenticator app or browser extension to scan.")}
          </Text>
        </Space>
        <div
          className={cls("security-2fa-qr-code", {
            centered: isQrCentered
          })}
        >
          {qrCodeSrc ? (
            <img src={qrCodeSrc} alt="2FA QR code" className="security-2fa-qr-image" />
          ) : otpauthUrl ? (
            <QRCodeSVG value={otpauthUrl} size={128} />
          ) : null}
        </div>

        <Text
          className={cls("security-2fa-setup-key-text", {
            centered: isQrCentered
          })}
        >
          {t("Unable to scan? You can use the")}{" "}
          <ButtonComponent
            type="link"
            onClick={() => setSecretModalOpen(true)}
            className="security-setup-key-btn"
          >
            {t("setup key")}
          </ButtonComponent>{" "}
          {t("to manually configure your authenticator app.")}
        </Text>

        <Space size={12} direction="vertical" className="security-2fa-block__verify">
          <Text className="security-verify-title">{t("Verify the code from the app")}</Text>

          <Flex gap={12}>
            {Array.from({ length: 6 }).map((_, index) => (
              <InputComponent
                key={index}
                ref={(node) => {
                  inputRefs.current[index] = node
                }}
                className="security-2fa-code-input"
                maxLength={1}
                value={authCodeDigits[index]}
                onChange={(event) => handleAuthCodeChange(index, event)}
                onKeyDown={(event) => handleAuthCodeKeyDown(index, event)}
                status={otpError ? "error" : undefined}
              />
            ))}
          </Flex>

          {otpError ? (
            <Text type="danger" className="security-2fa-error-text">
              {otpError}
            </Text>
          ) : null}
        </Space>

        {showTrustedDevice ? (
          <Flex align="center" gap={8}>
            <input
              type="checkbox"
              className="security-trusted-checkbox"
              checked={rememberDevice}
              onChange={(event) => setRememberDevice(event.target.checked)}
            />
            <Text className="security-trusted-text">
              {t("Remember this trusted device for 30 days")}
            </Text>
          </Flex>
        ) : null}

        <ButtonComponent
          type="primary"
          size="large"
          onClick={handleClick}
          block={isSaveButtonBlock}
          loading={isSubmitting}
          disabled={codeValue.length !== 6 || isSubmitting}
          className={cls("security-2fa-save-btn", {
            "with-top-margin": hasSaveButtonTopMargin
          })}
        >
          {t(buttonText)}
        </ButtonComponent>
      </Space>
      <TwoFactorSecretModal
        open={secretModalOpen}
        onClose={() => setSecretModalOpen(false)}
        setupKey={setupKey}
      />
    </>
  )
}

export default ScanCodeBlock
